// File: @/lib/analytics/debug-context.ts

interface AnalyticsDebugState {
  readonly pageLoadId: string;
  readonly sequence: number;
}

interface AnalyticsDebugCorrelation {
  readonly correlationId: string;
  readonly eventSequence: number;
  readonly pageLoadId: string;
  readonly runtime: 'client' | 'server';
}

// Debug logs from one page load share a pageLoadId and increasing sequence
// number, so a burst of events can be read back in order.

// eslint-disable-next-line functional/no-let -- module-local state is required for incrementing per-page-load debug sequence ids
let debugState: AnalyticsDebugState | null = null;

function getRuntime(): 'client' | 'server' {
  return typeof window === 'undefined' ? 'server' : 'client';
}

function createId(prefix: string): string {
  const random = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36);
  return `${prefix}_${time}_${random}`;
}

function createDebugState(runtime: 'client' | 'server'): AnalyticsDebugState {
  return {
    pageLoadId: createId(runtime === 'client' ? 'pg' : 'srv'),
    sequence: 0,
  };
}

function getDebugState(runtime: 'client' | 'server'): AnalyticsDebugState {
  if (debugState === null) {
    debugState = createDebugState(runtime);
  }

  return debugState;
}

export function getAnalyticsDebugCorrelation(): AnalyticsDebugCorrelation {
  const runtime = getRuntime();
  const state = getDebugState(runtime);
  const nextSequence = state.sequence + 1;
  debugState = {
    ...state,
    sequence: nextSequence,
  };

  return {
    correlationId: `${state.pageLoadId}:${nextSequence}`,
    eventSequence: nextSequence,
    pageLoadId: state.pageLoadId,
    runtime,
  };
}
